import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, Headset, Search, X } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import {
  HELP_ARTICLES,
  articlesForRoute,
  searchArticles,
  type HelpArticle,
} from '../../help/articles';
import Markdown from '../../help/Markdown';

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * The help centre behind the top bar's Support button: articles for the page
 * you're on first, then search and every article by category, and the contact
 * form as the way out when none of them answer it.
 */
const HelpDrawer = ({ open, onClose }: Props) => {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [slug, setSlug] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // An article about a page the user can't open would only lead to a dead end.
  const articles = useMemo(
    () => HELP_ARTICLES.filter((a) => !a.capability || can(a.capability)),
    [can],
  );
  const article = slug ? articles.find((a) => a.slug === slug) ?? null : null;
  const forThisPage = useMemo(() => articlesForRoute(articles, pathname), [articles, pathname]);
  const results = useMemo(() => searchArticles(articles, query), [articles, query]);
  const byCategory = useMemo(() => {
    const groups: { category: string; items: HelpArticle[] }[] = [];
    for (const a of articles) {
      const last = groups[groups.length - 1];
      if (last?.category === a.category) last.items.push(a);
      else groups.push({ category: a.category, items: [a] });
    }
    return groups;
  }, [articles]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    if (!slug) searchRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // Focus only on opening, not on every article change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [slug]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  if (!open) return null;

  const row = (a: HelpArticle) => (
    <li key={a.slug}>
      <button
        type="button"
        onClick={() => setSlug(a.slug)}
        className="group flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-900">{a.title}</span>
          {a.summary && <span className="mt-0.5 block text-xs text-slate-500">{a.summary}</span>}
        </span>
        <ChevronRight size={16} className="mt-0.5 shrink-0 text-slate-500 group-hover:text-slate-900" />
      </button>
    </li>
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Help">
      <button
        type="button"
        aria-label="Close help"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/30"
      />
      <div className="relative flex h-full w-full max-w-[440px] flex-col bg-white shadow-2xl">
        <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
          {article ? (
            <button
              type="button"
              onClick={() => setSlug(null)}
              className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft size={16} /> All articles
            </button>
          ) : (
            <h2 className="px-1 text-base font-bold text-slate-900">Help</h2>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close help"
            className="ml-auto inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          >
            <X size={18} />
          </button>
        </div>

        <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto">
          {article ? (
            <article className="px-5 py-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{article.category}</p>
              <h3 className="mt-1 text-xl font-bold text-slate-900">{article.title}</h3>
              {article.route && article.route !== pathname && (
                <button
                  type="button"
                  onClick={() => go(article.route!)}
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-slate-50"
                >
                  Open this page <ArrowRight size={14} />
                </button>
              )}
              <div className="mt-2">
                <Markdown source={article.body} onOpenArticle={setSlug} onNavigate={go} />
              </div>
            </article>
          ) : (
            <div className="px-4 py-4">
              <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-3 text-slate-500 focus-within:border-slate-400">
                <Search size={16} className="shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search help articles…"
                  aria-label="Search help articles"
                  className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-500"
                />
                {query && (
                  <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="text-slate-500 hover:text-slate-900">
                    <X size={14} />
                  </button>
                )}
              </label>

              {query ? (
                <section className="mt-4">
                  <h3 className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {results.length} result{results.length === 1 ? '' : 's'}
                  </h3>
                  {results.length > 0 ? (
                    <ul className="mt-1">{results.map(row)}</ul>
                  ) : (
                    <p className="px-3 py-3 text-sm text-slate-600">
                      Nothing matches “{query}”. Try fewer words, or contact support below.
                    </p>
                  )}
                </section>
              ) : (
                <>
                  {forThisPage.length > 0 && (
                    <section className="mt-4">
                      <h3 className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">For this page</h3>
                      <ul className="mt-1">{forThisPage.map(row)}</ul>
                    </section>
                  )}
                  {byCategory.map((group) => (
                    <section key={group.category} className="mt-4">
                      <h3 className="px-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{group.category}</h3>
                      <ul className="mt-1">{group.items.map(row)}</ul>
                    </section>
                  ))}
                </>
              )}
            </div>
          )}
        </div>

        <div className="border-t border-slate-200 px-4 py-3">
          <button
            type="button"
            onClick={() => go('/support')}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-slate-50"
          >
            <Headset size={18} className="shrink-0 text-slate-900" />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-slate-900">Still stuck? Contact support</span>
              <span className="block text-xs text-slate-500">Send our team a message about your issue</span>
            </span>
            <ChevronRight size={16} className="shrink-0 text-slate-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpDrawer;
